/**
 * Activity Timeline Component
 *
 * Displays chronological list of job activities with visual timeline.
 * PURELY PRESENTATIONAL - uses mocked data or passed activities prop.
 *
 * Features:
 * - Visual timeline with connecting lines
 * - Activity type icons and badges
 * - User avatars/initials
 * - Relative timestamps
 * - Grouping by date
 * - Empty state
 */

import * as React from 'react';
import type { JobActivity, ActivityType } from '@/types/job-activity';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatRelativeTime, groupByDate } from '@/lib/timeUtils';
import { useJobActivities } from '@/application/service-provider/hooks/useJobActivities';
import { mapActivityDtosToJobActivities } from '@/application/service-provider/mappers/ActivityMapper';

export interface ActivityTimelineProps {
  jobId: string;
}

/**
 * Get icon for activity type
 */
function getActivityIcon(activityType: ActivityType): string {
  switch (activityType) {
    case 'status_changed':
      return '🔄';
    case 'scheduled_changed':
      return '📅';
    case 'assigned':
      return '👤';
    case 'unassigned':
      return '👥';
    case 'custom_fields_updated':
      return '📝';
    case 'commented':
      return '💬';
    default:
      return '📌';
  }
}

/**
 * Get badge variant for activity type
 */
function getActivityBadgeVariant(
  activityType: ActivityType
): 'default' | 'secondary' | 'success' | 'warning' {
  switch (activityType) {
    case 'status_changed':
      return 'success';
    case 'scheduled_changed':
      return 'warning';
    case 'assigned':
      return 'default';
    case 'unassigned':
      return 'secondary';
    case 'custom_fields_updated':
      return 'secondary';
    case 'commented':
      return 'default';
    default:
      return 'secondary';
  }
}

/**
 * Get user initials from name
 */
function getUserInitials(userName: string | undefined): string {
  if (!userName) return 'U';
  const parts = userName.split(' ').filter(p => p.length > 0);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return userName.substring(0, Math.min(2, userName.length)).toUpperCase();
}

/**
 * Format activity description
 */
function getActivityDescription(activity: JobActivity): string {
  switch (activity.activityType) {
    case 'status_changed':
      return `changed status from ${activity.oldValue || 'unknown'} to ${activity.newValue || 'unknown'}`;
    case 'scheduled_changed':
      return `rescheduled from ${activity.oldValue || 'unknown'} to ${activity.newValue || 'unknown'}`;
    case 'assigned':
      return `assigned ${activity.fieldName || 'resource'}: ${activity.newValue || 'unknown'}`;
    case 'unassigned':
      return `unassigned ${activity.fieldName || 'resource'}`;
    case 'custom_fields_updated':
      return `updated ${activity.fieldName || 'field'} to ${activity.newValue || 'unknown'}`;
    case 'commented':
      return activity.comment || 'added a comment';
    default:
      return 'performed an action';
  }
}

export function ActivityTimeline({ jobId }: ActivityTimelineProps) {
  // Fetch activities from API
  const { data: activitiesData, isLoading, error } = useJobActivities(jobId);

  // Map DTOs to component types
  const activities = React.useMemo(() => {
    if (!activitiesData) return [];
    return mapActivityDtosToJobActivities(activitiesData);
  }, [activitiesData]);

  // Sort by timestamp (newest first)
  const sortedActivities = React.useMemo(() => {
    return [...activities].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [activities]);

  // Group by date
  const groupedActivities = React.useMemo(() => {
    return groupByDate(sortedActivities);
  }, [sortedActivities]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (activities.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {Array.from(groupedActivities.entries()).map(([date, dateActivities]) => (
        <div key={date}>
          {/* Date Header */}
          <div className="flex items-center gap-3 mb-4">
            <Separator className="flex-1" />
            <span className="text-sm font-medium text-gray-500">{date}</span>
            <Separator className="flex-1" />
          </div>

          {/* Activities for this date */}
          <div className="space-y-4">
            {dateActivities.map((activity, index) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                isLast={index === dateActivities.length - 1}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ActivityItemProps {
  activity: JobActivity;
  isLast: boolean;
}

function ActivityItem({ activity, isLast }: ActivityItemProps) {
  const icon = getActivityIcon(activity.activityType);
  const badgeVariant = getActivityBadgeVariant(activity.activityType);
  const initials = getUserInitials(activity.userName);
  const description = getActivityDescription(activity);
  const relativeTime = formatRelativeTime(activity.timestamp);

  return (
    <div className="flex gap-3">
      {/* Timeline with Avatar */}
      <div className="flex flex-col items-center">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        {!isLast && (
          <div className="w-[2px] bg-gray-200 flex-1 mt-2 min-h-[20px]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm text-gray-900">
                {activity.userName}
              </span>
              <Badge variant={badgeVariant} className="text-xs">
                <span className="mr-1">{icon}</span>
                {activity.activityType.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {relativeTime}
          </span>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-4">📋</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No Activity Yet
      </h3>
      <p className="text-sm text-gray-600">
        Activity will appear here as changes are made to this job.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="h-8 w-8 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ error }: { error: Error }) {
  const errorMessage = error.message.includes('401')
    ? 'Session expired. Please log in again.'
    : error.message.includes('403')
    ? "You don't have permission to view this."
    : error.message.includes('404')
    ? 'Job not found.'
    : 'Unable to load activities. Please check your connection.';

  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Error Loading Activities
      </h3>
      <p className="text-sm text-gray-600">{errorMessage}</p>
    </div>
  );
}
